// Generic AI Calculation Canvas widget (contract: type="calculation").
// ONE renderer for NoVA, Kaufnebenkosten, Finanzierung, Mietrendite, …
// The AI explains in chat; this visualises. Web + native share the contract.
import React, { useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Calculator, Receipt, Coins, Car, TriangleAlert, ChevronDown,
  ShieldCheck, CircleCheck, CircleAlert, Info, ExternalLink, Share2,
} from "lucide-react";
import { CalculationShareCard } from "./CalculationShareCard";
import { shareCalculationCard } from "../../../lib/calculationShare";

const SECTION_ICONS = {
  receipt: Receipt, coins: Coins, car: Car, alert: TriangleAlert,
  calculator: Calculator,
};

function fmtEur(v) {
  if (v == null || v === "") return "—";
  if (typeof v !== "number") return String(v);
  return `€ ${Math.round(v).toLocaleString("de-AT")}`;
}
function fmtValue(v, unit) {
  if (v == null || v === "") return "—";
  if (unit === "text") return String(v);
  if (unit === "eur_range") return `€ ${v}`;
  if (unit === "pct") return `${v} %`;
  return fmtEur(v);
}

const ACCENT = {
  amber: "text-amber-400",
  emerald: "text-emerald-400",
  rose: "text-rose-400",
  muted: "text-zinc-500",
};

function ValueRow({ row }) {
  const accent = ACCENT[row.accent] || "text-zinc-100";
  return (
    <div className="flex items-baseline justify-between gap-3 py-1" data-testid="calc-row">
      <span className={`text-sm ${row.strong ? "text-zinc-300" : "text-zinc-500"}`}>
        {row.label}
        {row.hint && <span className="ml-1 text-[11px] text-zinc-600">· {row.hint}</span>}
      </span>
      <span className={`text-right ${row.strong ? "text-base font-semibold" : "text-sm font-medium"} ${accent}`}>
        {fmtValue(row.value, row.unit)}
      </span>
    </div>
  );
}

function Section({ section }) {
  const [open, setOpen] = useState(section.default_open !== false);
  const Icon = SECTION_ICONS[section.icon] || Calculator;
  const body = (section.rows || []).map((r, i) => <ValueRow key={i} row={r} />);
  if (!section.collapsible) {
    return (
      <div className="py-1" data-testid={`calc-section-${section.key}`}>
        <div className="mb-0.5 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-zinc-500">
          <Icon className="h-3.5 w-3.5" />{section.label}
        </div>
        {body}
      </div>
    );
  }
  return (
    <div className="border-t border-zinc-800/60 pt-1" data-testid={`calc-section-${section.key}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`calc-section-toggle-${section.key}`}
        className="flex w-full items-center justify-between py-1.5 text-[11px] font-medium uppercase tracking-wide text-zinc-400 transition hover:text-zinc-200"
      >
        <span className="flex items-center gap-1.5">
          <Icon className="h-3.5 w-3.5" />{section.label}
        </span>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && <div className="pb-1">{body}</div>}
    </div>
  );
}

function ConfidenceBlock({ confidence, t }) {
  const [open, setOpen] = useState(false);
  if (!confidence) return null;
  const { score, level, factors = [] } = confidence;
  const dot = level === "high" ? "🟢" : level === "medium" ? "🟡" : "⚪";
  const barColor = level === "high" ? "bg-emerald-500" : level === "medium" ? "bg-amber-500" : "bg-zinc-500";
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-2.5" data-testid="calc-confidence">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2"
        data-testid="calc-confidence-toggle"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-zinc-200">
          <span aria-hidden>{dot}</span>
          {t("calc.confidence.title")} {score} %
        </span>
        <ChevronDown className={`h-4 w-4 text-zinc-500 transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-zinc-800">
        <div className={`h-full rounded-full ${barColor}`} style={{ width: `${score}%` }} />
      </div>
      {open && (
        <ul className="mt-2 space-y-1" data-testid="calc-confidence-factors">
          {factors.map((f) => (
            <li key={f.key} className="flex items-start gap-1.5 text-xs">
              {f.impact === "positive" ? (
                <CircleCheck className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-emerald-400" />
              ) : f.impact === "negative" ? (
                <CircleAlert className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-400" />
              ) : (
                <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-zinc-500" />
              )}
              <span className={f.impact === "negative" ? "text-amber-200/90" : "text-zinc-300"}>
                {f.label}
                {f.detail && <span className="text-zinc-600"> — {f.detail}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SourcesBlock({ sources, t }) {
  const [open, setOpen] = useState(false);
  if (!sources || sources.length === 0) return null;
  return (
    <div className="border-t border-zinc-800/60 pt-1.5" data-testid="calc-sources">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between py-1 text-xs font-medium text-zinc-400 transition hover:text-zinc-200"
        data-testid="calc-sources-toggle"
      >
        <span className="flex items-center gap-1.5">
          <ShieldCheck className="h-3.5 w-3.5 text-emerald-400/80" />
          {t("calc.sources.title")}
        </span>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && (
        <ul className="space-y-1 pb-1 pt-0.5">
          {sources.map((s) => (
            <li key={s.key} className="flex items-start gap-1.5 text-xs text-zinc-400">
              <span className="mt-0.5 text-emerald-400/70">✓</span>
              {s.url ? (
                <a
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex min-w-0 items-center gap-1 break-words text-zinc-300 underline decoration-zinc-700 underline-offset-2 [overflow-wrap:anywhere] hover:text-amber-300"
                >
                  {s.label}
                  <ExternalLink className="h-3 w-3" />
                </a>
              ) : (
                <span className="text-zinc-300">{s.label}</span>
              )}
              {s.detail && <span className="text-zinc-600">— {s.detail}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function CalculationWidget({ widget }) {
  const { t } = useTranslation("ai");
  const w = React.useMemo(() => widget || {}, [widget]);
  const shareRef = useRef(null);
  const [sharing, setSharing] = useState(false);
  const [shareMounted, setShareMounted] = useState(false);

  const onShare = useCallback(async () => {
    if (sharing) return;
    setSharing(true);
    setShareMounted(true);
    try {
      // let the off-screen card mount before rendering it to PNG
      await new Promise((r) => setTimeout(r, 80));
      await shareCalculationCard(shareRef.current, w);
    } finally {
      setShareMounted(false);
      setSharing(false);
    }
  }, [sharing, w]);

  if (!w.type) return null;
  const cmp = w.comparison;
  const delta = cmp?.delta;
  const verdict = w.verdict;

  return (
    <div
      data-testid="ai-widget-calculation"
      data-calc-kind={w.calc_kind}
      className="w-full max-w-md space-y-2 rounded-2xl border border-zinc-800 bg-zinc-900/70 p-3.5"
    >
      {/* header + share */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-sm font-semibold text-zinc-100">
            <Calculator className="h-4 w-4 flex-shrink-0 text-amber-400" />
            <span className="truncate">{w.title}</span>
          </div>
          {w.subtitle && <div className="truncate text-xs text-zinc-500">{w.subtitle}</div>}
        </div>
        <button
          type="button"
          onClick={onShare}
          disabled={sharing}
          data-testid="calc-share-btn"
          title={t("calc.share")}
          className="flex flex-shrink-0 items-center gap-1 rounded-lg border border-zinc-800 bg-zinc-800/40 px-2 py-1.5 text-xs text-zinc-300 transition hover:border-amber-500/40 hover:text-amber-300 disabled:opacity-50"
        >
          <Share2 className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">{t("calc.share")}</span>
        </button>
      </div>

      {/* sections */}
      <div>{(w.sections || []).map((s) => <Section key={s.key} section={s} />)}</div>

      {/* total */}
      {w.total && (
        <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.06] px-3 py-2" data-testid="calc-total">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-sm text-zinc-300">{w.total.label}</span>
            <span className="text-xl font-bold text-amber-400">{fmtValue(w.total.value, w.total.unit)}</span>
          </div>
        </div>
      )}

      {/* comparison + verdict */}
      {cmp && (
        <div className="space-y-1.5" data-testid="calc-comparison">
          <ValueRow row={{ label: cmp.sample ? `${cmp.label} · ${t("calc.sample", { count: cmp.sample })}` : cmp.label, value: cmp.market_value, unit: "eur" }} />
          {delta?.value != null && (
            <div className={`flex items-center justify-between rounded-lg px-3 py-2 ${delta.direction === "positive" ? "bg-emerald-500/10" : "bg-rose-500/10"}`}>
              <span className={`text-sm font-medium ${delta.direction === "positive" ? "text-emerald-300" : "text-rose-300"}`}>
                {delta.direction === "positive" ? "💰 " : "⚠️ "}{delta.label}
              </span>
              <span className={`text-lg font-bold ${delta.direction === "positive" ? "text-emerald-400" : "text-rose-400"}`}>
                {fmtEur(delta.value)}
              </span>
            </div>
          )}
        </div>
      )}
      {verdict && (
        <div
          data-testid="calc-verdict"
          className={`rounded-lg px-3 py-2 text-center text-sm font-semibold ${verdict.tone === "positive" ? "bg-emerald-500/15 text-emerald-300" : verdict.tone === "negative" ? "bg-rose-500/15 text-rose-300" : "bg-zinc-800 text-zinc-200"}`}
        >
          {verdict.tone === "positive" ? "✅ " : verdict.tone === "negative" ? "❌ " : ""}{verdict.label}
        </div>
      )}

      <ConfidenceBlock confidence={w.confidence} t={t} />
      <SourcesBlock sources={w.sources} t={t} />

      {w.disclaimer_key && (
        <div className="pt-0.5 text-[10px] text-zinc-600" data-testid="calc-disclaimer">
          {t(`calc.disclaimers.${w.disclaimer_key}`, { defaultValue: t("calc.disclaimers.default") })}
        </div>
      )}

      {/* off-screen share card (mounted only while sharing, rendered to PNG) */}
      {shareMounted && (
        <div style={{ position: "fixed", left: -99999, top: 0, pointerEvents: "none" }} aria-hidden>
          <div ref={shareRef}>
            <CalculationShareCard widget={w} />
          </div>
        </div>
      )}
    </div>
  );
}
